<!-- /review-status Page showing review status for a single review. It's used in two cases:
1. when user follows "check review status" from the submit confirmation page or from their confirmation email. 
2. when user clicks "view" on the review in thei /myreviews page.
User do not need to be registered to see this page. -->

<template>
  <div class="d-flex justify-center">
    <div class="main-card mt-3">
      <!-- when it's loading  -->
      <v-row justify="center">
        <v-col cols="12" md="8" lg="8" v-if="loading">
          <v-skeleton-loader type="card"> </v-skeleton-loader>
        </v-col>
      </v-row>
      <!-- when there is an error  -->
      <v-row v-if="errorMsg">
        <v-col
          cols="12"
          style="height: 50vh"
          class="d-flex justify-center align-center"
        >
          <v-row>
            <v-col
              cols="12"
              class="d-flex justify-center align-center ma-0 pa-0"
            >
              <v-icon x-large color="red darken-2">
                mdi-alert-circle-outline
              </v-icon>
            </v-col>
            <v-col
              cols="12"
              class="d-flex justify-center align-center ma-0 pa-0"
            >
              {{ errorMsg }}
            </v-col>
          </v-row>
        </v-col>
      </v-row>
      <!-- when all is ok -->
      <v-row justify="center" v-if="!errorMsg && !loading">
        <v-col cols="12" md="8" lg="8" class="ma-1">
          <v-card
            color="white mt-0"
            class="mb-5 mx-auto my-12"
            :class="{
              'left-border-orange': review.Tier === 1,
              'left-border-blue': review.Tier === 5,
              'left-border-purple': review.Tier === 10,
            }"
          >
            <v-card-text>
              <div class="d-flex">
                <div
                  class="font-weight-bold"
                  :class="{
                    'orange--text': review.Tier === 1,
                    'blue--text': review.Tier === 5,
                    'purple--text': review.Tier === 10,
                  }"
                >
                  Basic
                </div>
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <div class="ml-auto" v-bind="attrs" v-on="on">
                      {{ $utils.relativeTimeFromTs(review.UploadedTs) }}
                    </div>
                  </template>
                  <span>{{ $utils.timeFromTs(review.UploadedTs) }}</span>
                </v-tooltip>
              </div>
              <p class="text-h6 text--primary mb-2">
                {{ review.ResumeFileName }}
              </p>
              <v-row class="pt-0 mt-0 ml-1 mb-2">
                <div>{{ review.Email }}</div>
              </v-row>
              <v-row v-if="review.Status == 'reviewed'">
                <div class="text--secondary ml-3">
                  <v-icon left class="green--text">
                    mdi-check-circle-outline
                  </v-icon>
                  <span class="green--text">Reviewed</span>
                </div>
              </v-row>
              <v-row v-if="review.Status != 'reviewed'">
                <v-timeline dense clipped class="mt-2">
                  <v-timeline-item
                    small
                    :color="dotColorFromStatus('created')"
                    :class="{
                      'green--text font-weight-medium':
                        review.Status == 'created',
                    }"
                  >
                    Created
                  </v-timeline-item>
                  <v-timeline-item
                    small
                    :color="dotColorFromStatus('inreview')"
                    :class="{
                      'green--text font-weight-medium':
                        review.Status == 'inreview',
                    }"
                  >
                    Matched with reviewer
                  </v-timeline-item>
                  <v-timeline-item
                    small
                    :color="dotColorFromStatus('reviewed')"
                    :class="{
                      'green--text font-weight-medium':
                        review.Status == 'reviewed',
                    }"
                  >
                    Reviewed
                  </v-timeline-item>
                </v-timeline>
              </v-row>
            </v-card-text>

            <v-card-actions>
              <p
                class="text--disabled overline mb-0 pb-0"
                style="font-size: 0.6rem !important"
              >
                RevId: {{ review.Id }}
              </p>
              <v-spacer></v-spacer>

              <!-- TODO: figure out how to show/download the file w/o it being on googleapis website -->
              <!-- <a
                :href="review.ResumeUrl"
                target="_blank"
                class="text-decoration-none"
              >
                Download review
              </a> -->
              <v-btn v-if="review.IsReviewed" icon @click="show = !show">
                <v-icon>{{
                  show ? "mdi-chevron-up" : "mdi-chevron-down"
                }}</v-icon>
              </v-btn>
            </v-card-actions>

            <v-expand-transition>
              <div v-show="show">
                <v-divider></v-divider>
                <v-card-text>
                  <v-row>
                    <v-col cols="12" class="pl-0">
                      <div class="d-flex mb-2">
                        <v-icon class="mr-1">mdi-code-tags</v-icon>
                        <h3 class="">AI analysis</h3>
                      </div>
                      <v-row>
                        <v-col
                          cols="12"
                          class="d-flex flex-row align-center ml-2"
                        >
                          <div class="mr-2">Parsability</div>
                          <v-rating
                            :value="4"
                            color="amber"
                            dense
                            half-increments
                            readonly
                            size="20"
                          >
                          </v-rating>
                          <v-spacer></v-spacer>
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-icon
                                small
                                v-bind="attrs"
                                v-on="on"
                                class="pb-1 ml-2"
                              >
                                mdi-information-outline
                              </v-icon>
                            </template>
                            <span>
                              How well your resume will be scanned and analyzed
                              by automated resume screening systems, commonly
                              used by companies for initial filtering.
                            </span>
                          </v-tooltip>
                        </v-col>
                      </v-row>

                      <div
                        class="mt-1 ml-2"
                        :class="{ 'text-truncate': !parsabilityShowMore }"
                      >
                        {{ parsabilitySummary }}
                      </div>

                      <a
                        href="#"
                        class="text-decoration-none font-weight-light float-right"
                        @click="parsabilityShowMore = !parsabilityShowMore"
                      >
                        {{ showMoreShowLess(parsabilityShowMore) }}
                      </a>
                    </v-col>
                    <v-col cols="12" class="pl-0">
                      <div class="d-flex">
                        <v-icon class="mr-1">mdi-account-outline</v-icon>
                        <h3>Reviewer feedback</h3>
                        <v-tooltip top>
                          <template v-slot:activator="{ on, attrs }">
                            <div
                              class="ml-auto text--secondary"
                              v-bind="attrs"
                              v-on="on"
                            >
                              {{ $utils.relativeTimeFromTs(review.ReviewedTs) }}
                            </div>
                          </template>
                          <span>{{
                            $utils.timeFromTs(review.ReviewedTs)
                          }}</span>
                        </v-tooltip>
                      </div>
                    </v-col>
                    <v-col cols="12">
                      <v-row>
                        <v-col
                          cols="12"
                          class="d-flex flex-row align-center mt-0 mb-1 pt-0 pb-0"
                        >
                          <h4 class="mr-2">Overall</h4>
                          <v-rating
                            :value="review.ScoreOverall"
                            color="amber"
                            dense
                            half-increments
                            readonly
                            size="20"
                          >
                          </v-rating>
                        </v-col>
                        <v-col cols="12" class="mt-0 pt-0 mb-1">
                          Lorem ipsum dolor sit amet, consectetur adipiscing
                          elit, sed do eiusmod tempor incididunt ut labore et
                          dolore magna aliqua. Ut enim ad minim veniam, quis
                          nostrud exercitation ullamco laboris nisi ut aliquip
                          ex ea commodo consequat. Duis aute irure dolor in
                          reprehenderit in voluptate velit esse cillum dolore eu
                          fugiat nulla pariatur. Excepteur sint occaecat
                          cupidatat non proident, sunt in culpa qui officia
                          deserunt mollit anim id est laborum.
                        </v-col>
                      </v-row>
                      <v-row class="d-flex flex-row mt-1">
                        <v-col cols="12" class="pt-0">
                          <v-chip
                            class="mr-2"
                            color="green"
                            outlined
                            small
                            v-for="pos in review.NotesPositive"
                            :key="pos"
                          >
                            {{ pos }}
                          </v-chip>
                          <v-chip
                            class="mr-2"
                            color="orange"
                            outlined
                            small
                            v-for="pos in review.NotesNegative"
                            :key="pos"
                          >
                            {{ pos }}
                          </v-chip>
                        </v-col>
                      </v-row>
                      <!-- visual feedback (Tier 5 and 10 only) -->
                      <v-divider
                        v-if="review.Tier === 5 || review.Tier === 10"
                        class="mt-3 mb-3"
                      ></v-divider>
                      <v-row v-if="review.Tier === 5 || review.Tier === 10">
                        <v-col cols="12" class="d-flex flex-row">
                          <h4 class="mr-2">Form</h4>
                          <v-rating
                            :value="review.ScoreVisual"
                            color="amber"
                            dense
                            half-increments
                            readonly
                            size="20"
                          >
                          </v-rating>
                          <div class="grey--text ms-2">
                            {{ review.ScoreVisual }}
                          </div>
                        </v-col>
                        <v-col cols="12" class="pt-0">
                          <blockquote class="text--secondary">
                            {{ review.CommentVisual }}
                          </blockquote>
                        </v-col>
                      </v-row>
                      <v-divider
                        v-if="review.Tier === 5 || review.Tier === 10"
                        class="mt-3 mb-3"
                      ></v-divider>
                      <v-row v-if="review.Tier === 5 || review.Tier === 10">
                        <v-col cols="12" class="d-flex flex-row">
                          <h4 class="mr-2">Content</h4>
                          <v-rating
                            :value="review.ScoreContent"
                            color="amber"
                            dense
                            half-increments
                            readonly
                            size="20"
                          >
                          </v-rating>
                          <div class="grey--text ms-2">
                            {{ review.ScoreContent }}
                          </div>
                        </v-col>
                        <v-col cols="12" class="pt-0">
                          <blockquote class="text--secondary">
                            {{ review.CommentContent }}
                          </blockquote>
                        </v-col>
                      </v-row>
                      <v-divider class="mt-3 mb-3"></v-divider>
                      <v-row>
                        <v-col cols="12" class="d-flex flex-row mb-0 pb-0">
                          <v-spacer></v-spacer>
                          <div class="mr-2">Reviewed by</div>
                          <v-chip
                            class="mr-2"
                            color="blue"
                            small
                            outlined
                            pill
                            :href="`/rev-profile?id=${reviewerId}`"
                          >
                            <v-icon left> mdi-account-outline </v-icon>
                            {{ reviewerPubName }}
                          </v-chip>
                        </v-col>
                        <!-- rate reviewer UI -->
                        <!-- <v-col class="mt-1 pt-0 d-flex flex-row center">
                          <a
                            v-if="!rateReviewer && !rateReviewerDone"
                            @click="rateReviewer = true"
                            class="text-decoration-none font-weight-light mr-3"
                          >
                            rate reviewer
                          </a>
                          <v-rating
                            v-model="reviewerRating"
                            color="blue"
                            dense
                            half-increments
                            size="20"
                            v-if="rateReviewer || rateReviewerDone"
                            :readonly="rateReviewerDone"
                          ></v-rating>
                          <v-btn
                            class="mt-1"
                            v-if="rateReviewer && !rateReviewerDone"
                            color="blue"
                            x-small
                            text
                            @click="saveRateReviewer()"
                          >
                            save
                          </v-btn>
                        </v-col> -->
                      </v-row>
                    </v-col>
                  </v-row>
                </v-card-text>
              </div>
            </v-expand-transition>
          </v-card>
        </v-col>
        <v-col cols="12" md="8" lg="8" class="ma-1">
          <ReportProblem
            styleClass="text-decoration-none font-weight-light float-right"
          ></ReportProblem>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
import ReportProblem from "../components/ReportProblem.vue";

export default {
  components: {
    ReportProblem,
  },
  data() {
    return {
      loading: true,
      show: false,
      review: {},
      rating: 0,
      reviewRef: "",
      reviewId: "",
      errorMsg: "",
      // reviewer stuff
      reviewerPubName: "",
      reviewerId: "",
      dialog: false,
      reviewerRating: 0,
      rateReviewer: false,
      rateReviewerDone: false,
      parsabilitySummary:
        "The resume demonstrates good parsability due to its clear section headings (Skills, Experience, Education, Projects), consistent formatting, and chronological work experience presentation.  The use of bullet points enhances readability for both humans and ATS. However, the formatting of the company name and dates within the experience section could be improved for more consistent parsing. While the information is presented logically, minor inconsistencies might slightly hinder perfect parsing.  The absence of complex layouts and the predominantly text-based nature of the document contribute positively to its overall score.  More structured formatting of dates and company details would significantly boost its ATS compatibility.  Consider using a standard format like YYYY-MM-DD for dates and bolding company names for better extraction. Overall, the resume is well-structured and has a high likelihood of being parsed correctly, but minor improvements would make it even more ATS-friendly.",
      parsabilityShowMore: false,
    };
  },
  async mounted() {
    this.$utils.visit(this);
    this.reviewRef = this.$route.query.reviewRef;
    this.reviewId = this.$route.query.reviewId;
    await this.getReviewStatus();
  },
  methods: {
    async getReviewStatus() {
      try {
        this.loading = true;
        if (!this.reviewRef && !this.reviewId) {
          throw new Error(
            "Looks like this link is invalid (no reviewRef or reviewId)"
          );
        }
        var resp = await this.$utils.callBackendPost(
          this,
          "/pub/getReviewStatus",
          { reviewRef: this.reviewRef, reviewId: this.reviewId }
        );
        console.log("got review status: " + JSON.stringify(resp));
        this.review = resp.review;
        this.show = this.review.IsReviewed;
        // reviewer stuff
        this.reviewerPubName = resp.reviewer.PublicName;
        this.reviewerId = resp.reviewer.Id;
      } catch (error) {
        this.errorMsg = error;
      } finally {
        this.loading = false;
      }
    },
    saveRateReviewer() {
      this.rateReviewerDone = true;
      console.log("rate reviewer: " + this.reviewerRating);
    },
    dotColorFromStatus(cs) {
      if (this.review.Status == "created") {
        if (cs == "inreview" || cs == "reviewed") {
          return "grey";
        }
        return "green";
      }
      if (this.review.Status == "inreview") {
        if (cs == "inreview" || cs == "created") {
          return "green";
        } else {
          return "grey";
        }
      }
      if (this.review.Status == "reviewed") {
        return "green";
      }
    },
    showMoreShowLess(showMore) {
      if (showMore) {
        return "show less";
      } else {
        return "show more";
      }
    },
  },
};
</script>

<style scoped>
.main-card {
  width: 100vw;
  max-width: 1200px;
}
.left-border-orange {
  border-top: 10px solid orange !important;
}
.left-border-blue {
  border-top: 10px solid blue !important;
}
.left-border-purple {
  border-top: 10px solid purple !important;
}
</style>
